import React from 'react';
import { Calendar, Edit3, CheckCircle } from 'lucide-react';
import StarRating from './StarRating';
import { useLanguage } from '../../hooks/useLanguage';

// エピソード行コンポーネント
const EpisodeRow = ({ episode, onRate, ratingMode = 'simple', isDropped = false }) => {
  const { t } = useLanguage();
  // 評価があるかどうかを判定
  const hasRating = ratingMode === 'detailed' 
    ? episode.detailedRating && (
        episode.detailedRating.content > 0 || 
        episode.detailedRating.animation > 0 || 
        episode.detailedRating.sound > 0
      )
    : episode.rating > 0;
    
  // 平均評価を計算
  const getAverageRating = () => {
    if (ratingMode === 'detailed' && episode.detailedRating) {
      const ratings = [
        episode.detailedRating.content,
        episode.detailedRating.animation,
        episode.detailedRating.sound
      ].filter(r => r > 0);
      
      return ratings.length > 0 ? ratings.reduce((a, b) => a + b) / ratings.length : 0;
    }
    return episode.rating || 0;
  };

  const avgRating = getAverageRating();

  return (
    <div className={`flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700 rounded-lg mb-3 hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors ${
      isDropped ? 'opacity-50' : ''
    }`}>
      <div className="flex-1">
        <div className="flex items-center gap-3 mb-2">
          <span className="text-blue-600 dark:text-blue-400 font-bold text-sm bg-blue-100 dark:bg-blue-900/30 px-2 py-1 rounded">
            {t('episode', { number: episode.number || 0 })}
          </span>
          {episode.storyTitle && (
            <span className="text-gray-600 dark:text-gray-300 text-sm">- {episode.storyTitle}</span>
          )}
          {hasRating && (
            <CheckCircle className="text-green-600" size={16} />
          )}
        </div>
        
        {hasRating ? (
          <>
            <div className="flex items-center gap-2">
              <StarRating rating={avgRating} size={14} allowHalf={true} />
              <span className="text-sm font-medium">{avgRating.toFixed(1)}</span>
              
              {/* 詳細評価の場合は各項目の評価を表示 */}
              {ratingMode === 'detailed' && episode.detailedRating && (
                <span className="text-xs text-gray-500 dark:text-gray-400 ml-2">
                  ({t('content')}: {episode.detailedRating.content ? episode.detailedRating.content.toFixed(1) : '-'} / 
                   {t('animation')}: {episode.detailedRating.animation ? episode.detailedRating.animation.toFixed(1) : '-'} / 
                   {t('sound')}: {episode.detailedRating.sound ? episode.detailedRating.sound.toFixed(1) : '-'})
                </span>
              )}
              
              <span className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1">
                <Calendar size={12} />
                {episode.watchedDate && new Date(episode.watchedDate).toLocaleDateString(t('language') === 'ja' ? 'ja-JP' : 'en-US')}
              </span>
            </div>
          </>
        ) : (
          <span className="text-gray-400 dark:text-gray-500 text-sm">{t('unwatched')}</span>
        )}
        
        {episode.notes && (
          <p className="text-xs text-gray-600 dark:text-gray-300 mt-1 bg-yellow-50 dark:bg-yellow-900/30 p-2 rounded border-l-2 border-yellow-300 dark:border-yellow-600">
            {episode.notes}
          </p>
        )}
      </div>

      {!isDropped && (
        <button
          onClick={() => onRate(episode)}
          className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 p-2 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-all"
          title={hasRating ? t('editRating') : t('rateEpisode')}
        >
          <Edit3 size={18} />
        </button>
      )}
    </div>
  );
};

export default EpisodeRow;